import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../services/supabase';
import { apiService } from '../services/api';
import { joinPersonName, splitPersonName } from '../utils/personName';

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userDataError, setUserDataError] = useState(null);
  const [isRecoverySession, setIsRecoverySession] = useState(
    () => sessionStorage.getItem('isRecoverySession') === 'true'
  );
  // Refs used by the auth listener (subscribed once, so it can't read fresh state).
  const lastUserIdRef = useRef(null);
  const userDataRef = useRef(null);
  useEffect(() => { userDataRef.current = userData; }, [userData]);

  const fetchUserData = async (supabaseUser) => {
    try {
      const data = await apiService.getUserByEmail(supabaseUser.email);
      setUserData(data);
    } catch (error) {
      if (error.code === 'USER_NOT_FOUND') {
        // Check providers[] array (most reliable — app_metadata.provider can be 'email' even for Google users
        // when the Supabase account was originally email-linked)
        const providersList = supabaseUser.app_metadata?.providers || [];
        const provider =
          providersList.find(p => p !== 'email') ||
          (supabaseUser.user_metadata?.iss?.includes('google') ? 'google' : null) ||
          (supabaseUser.user_metadata?.provider_id ? 'google' : null);

        console.log('[AuthContext] USER_NOT_FOUND — provider detection:', {
          app_metadata_provider: supabaseUser.app_metadata?.provider,
          providers_list: providersList,
          user_metadata_iss: supabaseUser.user_metadata?.iss,
          resolved_provider: provider,
        });
        if (provider) {
          // New OAuth user — create backend account automatically
          try {
            const created = await apiService.syncOAuthUser(provider, supabaseUser);
            // ── Read the account back, rather than trusting the create reply ──
            // /auth/oauth-callback returns a SHORT record — id, email, name,
            // api_key, plan_id, avatar_url — while /users/by-email returns the
            // full one. Using the short reply directly left fields simply
            // ABSENT for the whole of a new user's first session, and absent is
            // not the same as false: `has_password` came back undefined, which
            // Settings has to treat as "has a password" (it cannot risk
            // skipping the current-password check), so a brand-new Google
            // account was asked for a password it had never set.
            //
            // Re-reading gives every account the same shape from one endpoint,
            // whatever route it arrived by. Falls back to the create reply if
            // the read fails — a usable session beats none.
            const data = created
              ? (await apiService.getUserByEmail(supabaseUser.email).catch(() => null)) || created
              : created;
            setUserData(data);
          } catch (syncError) {
            console.error('Failed to create OAuth user account:', syncError);
            setUserDataError('Failed to create your account. Please try again.');
            await supabase.auth.signOut();
            setUserData(null);
          }
        } else {
          // Email/password user with no backend account
          setUserDataError('No ImproveSEO account found for this email. Please sign up first.');
          await supabase.auth.signOut();
          setUserData(null);
        }
      } else {
        console.error('Failed to fetch user data:', error);
        setUserData(null);
      }
    }
  };

  const clearUserDataError = () => setUserDataError(null);

  const refreshUserData = async () => {
    if (user) {
      await fetchUserData(user);
    }
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      lastUserIdRef.current = session?.user?.id ?? null;
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserData(session.user);
      }
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.email);

        if (event === 'PASSWORD_RECOVERY') {
          setIsRecoverySession(true);
          sessionStorage.setItem('isRecoverySession', 'true');
        }
        if (event === 'SIGNED_OUT') {
          setIsRecoverySession(false);
          sessionStorage.removeItem('isRecoverySession');
        }

        // supabase-js re-emits SIGNED_IN / TOKEN_REFRESHED every time the tab
        // regains focus. Setting user/userData to fresh objects on each of those
        // re-renders the whole tree and re-runs every effect keyed on userData,
        // which makes pages (and open dialogs) visibly "reload" on tab switch.
        // Only act when the signed-in user actually changed, their profile was
        // updated, or the earlier userData fetch never succeeded (retry).
        const nextUserId = session?.user?.id ?? null;
        const userChanged = nextUserId !== lastUserIdRef.current;
        const needsRetry = !!session?.user && !userDataRef.current;
        if (!userChanged && !needsRetry && event !== 'USER_UPDATED') {
          return;
        }
        lastUserIdRef.current = nextUserId;
        setUser(session?.user ?? null);

        if (session?.user) {
          await fetchUserData(session.user);
        } else {
          setUserData(null);
        }
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
    // Deliberately runs once: this SUBSCRIBES to auth changes, and the cleanup
    // unsubscribes. fetchUserData is redefined on every render, so listing it
    // would tear down and re-establish the auth subscription on each one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  };

  /**
   * Register, carrying the profile details the account will need later.
   *
   * `profile` is { first, last }. It used to be a single `name`
   * string; the parts are collected separately now because splitting a name
   * afterwards needs a heuristic that is wrong for a great many real names.
   *
   * ── WHY THIS GOES INTO user_metadata ────────────────────────────────────
   * Supabase creates the AUTH user here, but our own user row does not exist
   * until the email is confirmed and the account is first loaded (see
   * fetchUserData's USER_NOT_FOUND path). There is nowhere to write a profile
   * to yet, so it rides along on the auth user and is applied on first load.
   *
   * `name` is still written alongside the parts because the Dashboard
   * greeting, the header chip and the avatar initials all read it — the split
   * is real at the edges and joined in storage, which is the contract stated
   * in personName.js.
   */
  const signUp = async (email, password, profile = {}) => {
    // Tolerates the old string form, so any caller not yet updated still works.
    const parts = typeof profile === 'string'
      ? splitPersonName(profile)
      : { first: profile.first || '', last: profile.last || '' };

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name: joinPersonName(parts.first, parts.last),
          first_name: parts.first,
          last_name: parts.last,
        }
      }
    });
    return { data, error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resendConfirmation = async (email) => {
    const { data, error } = await supabase.auth.resend({
      type: 'signup',
      email
    });
    return { data, error };
  };

  const sendMagicLink = async (email, redirectPath = '/auth/callback') => {
    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}${redirectPath}`,
      },
    });
    return { data, error };
  };

  const sendPasswordReset = async (email, redirectPath = '/update-password') => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}${redirectPath}`,
    });
    return { data, error };
  };

  const updatePassword = async (newPassword) => {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { data, error };
  };

  const signInWithGoogle = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });
      if (error) return { data: null, error };
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const value = {
    user,
    userData,
    loading,
    userDataError,
    isRecoverySession,
    clearUserDataError,
    signIn,
    signUp,
    signInWithGoogle,
    sendMagicLink,
    sendPasswordReset,
    updatePassword,
    signOut,
    resendConfirmation,
    refreshUserData,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};